import { apiFetch } from "../client"

interface PaginationMeta {
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}

export interface MicroLoanTierState {
  clientId: string
  currentTier: string
  tierName: string
  tierScore: number
  isFrozen: boolean
  freezeReason: string | null
  terms: {
    principalCeiling: number
    principalCeilingDisplay: string
    minTenorDays: number
    maxTenorDays: number
    defaultTenorDays: number
    applicationFeeRate: number
    interestRate: number
    requiredCycles: number
  }
  nextTier: {
    tier: string
    tierName: string | null
    remainingCycles: number
    principalCeiling: number | null
    principalCeilingDisplay: string | null
  } | null
  activeMicroLoan?: {
    loanId: string
    principalAmount: number
    applicationFeeAmount: number
    netDisbursedAmount: number | null
    interestRate: number
    termDays: number
    lifecycleStage: string
    overdueCategory: string | null
    dueDate: string | null
    disbursementDate: string | null
    closedAt: string | null
    microLoanTier: string | null
  } | null
  latestChange?: {
    direction: string
    reason: string
    createdAt: string
    note: string | null
  } | null
}

export interface MicroLoanTierHistoryEntry {
  id: string
  clientId: string
  clientPublicId: string
  tierFrom: string | null
  tierTo: string
  direction: string
  reason: string
  triggerLoanId: string | null
  triggerLoanPublicId: string | null
  actorUserId: string | null
  actorPublicId: string | null
  actorName: string | null
  note: string | null
  createdAt: string
}

export interface ClientMicroLoanHistoryEntry {
  loanId: string
  principalAmount: number
  applicationFeeAmount: number
  netDisbursedAmount: number | null
  interestRate: number
  termDays: number
  lifecycleStage: string
  overdueCategory: string | null
  dueDate: string | null
  disbursementDate: string | null
  closedAt: string | null
  microLoanTier: string | null
  onTimeFlag: boolean | null
}

export async function listClients(params?: Record<string, string>) {
  const query = params ? `?${new URLSearchParams(params).toString()}` : ""
  return apiFetch<any>(`/clients${query}`)
}

export async function getClient(clientId: string) {
  return apiFetch<any>(`/clients/${clientId}`)
}

export async function listClientFlags(clientId: string) {
  return apiFetch<any>(`/clients/${clientId}/flags`)
}

export async function getClientMicroLoanTier(clientId: string) {
  return apiFetch<{ data: MicroLoanTierState }>(`/clients/${clientId}/micro-loan-tier`)
}

export async function listClientMicroLoanTierHistory(clientId: string) {
  return apiFetch<{ data: MicroLoanTierHistoryEntry[] }>(`/clients/${clientId}/micro-loan-tier/history`)
}

export async function listClientMicroLoans(
  clientId: string,
  params?: { page?: number; perPage?: number },
) {
  const query = new URLSearchParams()
  if (params?.page) {
    query.set("page", String(params.page))
  }
  if (params?.perPage) {
    query.set("per_page", String(params.perPage))
  }

  const suffix = query.toString() ? `?${query.toString()}` : ""
  return apiFetch<{ data: ClientMicroLoanHistoryEntry[]; meta: PaginationMeta }>(
    `/clients/${clientId}/micro-loans${suffix}`,
  )
}

export async function updateClientMicroLoanTier(clientId: string, payload: object) {
  return apiFetch<{ data: MicroLoanTierState }>(`/clients/${clientId}/micro-loan-tier`, {
    method: "PATCH",
    rawBody: payload,
  })
}

export async function createClient(payload: object) {
  return apiFetch<any>("/clients", {
    method: "POST",
    rawBody: payload,
  })
}

export async function updateClient(clientId: string, payload: object) {
  return apiFetch<any>(`/clients/${clientId}`, {
    method: "PATCH",
    rawBody: payload,
  })
}

export async function assignClient(clientId: string, agentId: string) {
  return apiFetch<any>(`/clients/${clientId}/assign`, {
    method: "POST",
    rawBody: { agentId },
  })
}

export async function createClientFlag(
  clientId: string,
  payload: { reason: string; note?: string },
) {
  return apiFetch<any>(`/clients/${clientId}/flags`, {
    method: "POST",
    rawBody: payload,
  })
}

export async function resolveClientFlag(
  clientId: string,
  flagId: string,
  payload: { status: "RESOLVED"; resolutionNote?: string },
) {
  return apiFetch<any>(`/clients/${clientId}/flags/${flagId}`, {
    method: "PATCH",
    rawBody: payload,
  })
}

export async function bulkAssignClients(payload: {
  clientIds: string[]
  agentId: string
  note?: string
}) {
  return apiFetch<any>("/clients/assignments/bulk", {
    method: "POST",
    rawBody: payload,
  })
}

export async function transferAssignments(payload: {
  sourceAgentId: string
  destinationAgentId: string
  clientIds?: string[]
  note?: string
}) {
  return apiFetch<any>("/clients/assignments/transfer", {
    method: "POST",
    rawBody: payload,
  })
}

export async function redistributeAssignments(payload: {
  sourceAgentId: string
  teamName?: string
  note?: string
}) {
  return apiFetch<any>("/clients/assignments/redistribute", {
    method: "POST",
    rawBody: payload,
  })
}
